import type {
  AdminLearnerDetail, AdminOverview, AdminUsersResponse, AuditEvent, AuthStepResponse,
  CapstoneDetail, CapstoneRow, CapstoneSubmissionMine, CatalogueResponse, CertificateResponse, CourseResponse,
  CreatedInvite, EnrolConfirmResponse, ForumPostAdmin, ForumThreadAdmin, RequestStatus, TutorialRequest, EnrolStartResponse, FileContent, FsEntry, GradeResult,
  FeedbackAdminItem, FeedbackItem, FeedbackKind, FeedbackStatus, HintResponse, IdentitiesResponse, InviteRow, MeResponse, ModuleContent, ModuleDetailProgress, Progress,
  QuizSubmission, Role, SandboxStatus, SessionRow, SubmitResponse,
} from "./types";

/** Every error the API can hand back, in a form the UI can branch on. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly payload: Record<string, unknown>;

  constructor(status: number, code: string, message: string, payload: Record<string, unknown> = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.payload = payload;
  }

  /** True when the session is gone or has not cleared MFA — the router has to intervene. */
  get isAuthProblem(): boolean {
    return (
      this.status === 401
      || this.code === "unauthenticated"
      || this.code === "mfa_required"
      || this.code === "mfa_enrolment_required"
    );
  }

  get isSandboxUnavailable(): boolean {
    return this.code === "sandbox_unavailable" || this.code === "sandbox_failed";
  }
}

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

/**
 * Called whenever the API says the session is gone or has not cleared MFA, so the session
 * context can re-check itself and the route guards can send the learner to the right screen
 * instead of every screen showing its own "sign in to continue".
 */
let authProblemHandler: (() => void) | null = null;

export function setAuthProblemHandler(handler: (() => void) | null): void {
  authProblemHandler = handler;
}

interface RequestOptions {
  method?: Method;
  body?: unknown;
  query?: Record<string, string | number | undefined>;
  signal?: AbortSignal;
}

function withQuery(path: string, query?: RequestOptions["query"]): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== "") params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

/**
 * One place that knows the request conventions: the CSRF header on anything that changes
 * state, the session cookie, and `{ error, message }` turned into an ApiError.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? "GET";
  const headers: Record<string, string> = { accept: "application/json" };
  const mutating = method !== "GET";
  if (mutating) headers["x-requested-with"] = "tfl";
  if (options.body !== undefined) headers["content-type"] = "application/json";

  let response: Response;
  try {
    response = await fetch(withQuery(path, options.query), {
      method,
      headers,
      credentials: "same-origin",
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
    throw new ApiError(0, "network", "The server could not be reached. Check your connection and try again.");
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let parsed: unknown = undefined;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = undefined;
    }
  }

  if (!response.ok) {
    const body = (parsed ?? {}) as Record<string, unknown>;
    const code = typeof body.error === "string" ? body.error : `http_${response.status}`;
    const message = typeof body.message === "string"
      ? body.message
      : `The server returned an unexpected ${response.status} response.`;
    const error = new ApiError(response.status, code, message, body);
    if (error.isAuthProblem && path !== "/api/auth/me") authProblemHandler?.();
    throw error;
  }

  if (parsed === undefined) {
    throw new ApiError(response.status, "bad_response", "The server returned a response we could not read.");
  }
  return parsed as T;
}

const get = <T>(path: string, query?: RequestOptions["query"], signal?: AbortSignal) =>
  request<T>(path, { query, signal });
const post = <T>(path: string, body?: unknown) => request<T>(path, { method: "POST", body });

export const api = {
  // ---------------------------------------------------------------- auth
  me: (signal?: AbortSignal) => get<MeResponse>("/api/auth/me", undefined, signal),

  register: (body: { email: string; password: string; displayName: string; inviteToken?: string }) =>
    post<AuthStepResponse>("/api/auth/register", body),

  login: (body: { email: string; password: string }) =>
    post<AuthStepResponse>("/api/auth/login", body),

  logout: () => post<{ ok: true }>("/api/auth/logout"),

  mfaEnrolStart: () => post<EnrolStartResponse>("/api/auth/mfa/enrol/start"),

  mfaEnrolConfirm: (code: string) =>
    post<EnrolConfirmResponse>("/api/auth/mfa/enrol/confirm", { code }),

  mfaVerify: (body: { code: string } | { recoveryCode: string }) =>
    post<AuthStepResponse>("/api/auth/mfa/verify", body),

  /** A password, or for an account without one, a current authenticator code. */
  regenerateRecoveryCodes: (proof: { password: string } | { code: string }) =>
    post<{ recoveryCodes: string[] }>("/api/auth/mfa/recovery/regenerate", proof),

  /** `currentPassword` is left out when setting a first password. */
  changePassword: (body: { currentPassword?: string; newPassword: string }) =>
    post<{ ok: true }>("/api/auth/password/change", body),

  forgotPassword: (email: string) => post<{ ok: true }>("/api/auth/password/forgot", { email }),

  resetPassword: (body: { token: string; newPassword: string }) =>
    post<{ ok: true }>("/api/auth/password/reset", body),

  sessions: (signal?: AbortSignal) =>
    get<{ sessions: SessionRow[] }>("/api/auth/sessions", undefined, signal),

  revokeSession: (id: string) =>
    request<{ ok: true }>(`/api/auth/sessions/${encodeURIComponent(id)}`, { method: "DELETE" }),

  identities: (signal?: AbortSignal) =>
    get<IdentitiesResponse>("/api/auth/oauth/identities", undefined, signal),

  unlinkIdentity: (provider: string) =>
    request<{ ok: true }>(`/api/auth/oauth/identities/${encodeURIComponent(provider)}`, { method: "DELETE" }),

  // ---------------------------------------------------------------- course
  catalogue: (signal?: AbortSignal) => get<CatalogueResponse>("/api/courses", undefined, signal),

  course: (courseId: string, signal?: AbortSignal) =>
    get<CourseResponse>(`/api/courses/${encodeURIComponent(courseId)}`, undefined, signal),

  progress: (signal?: AbortSignal) => get<Progress>("/api/course/progress", undefined, signal),

  moduleProgress: (moduleId: string, signal?: AbortSignal) =>
    get<ModuleDetailProgress>(
      `/api/course/modules/${encodeURIComponent(moduleId)}/progress`, undefined, signal,
    ),

  completeLesson: (lessonId: string) =>
    post<{ ok: true; progress: Progress }>(
      `/api/course/lessons/${encodeURIComponent(lessonId)}/complete`,
    ),

  submitQuiz: (quizId: string, answers: Record<string, number[]>) =>
    post<QuizSubmission>(`/api/course/quizzes/${encodeURIComponent(quizId)}/submit`, { answers }),

  labResult: (labId: string, signal?: AbortSignal) =>
    get<{ result: GradeResult | null; gradedAt?: number }>(
      `/api/course/labs/${encodeURIComponent(labId)}/result`, undefined, signal,
    ),

  hints: (labId: string, signal?: AbortSignal) =>
    get<HintResponse>(`/api/course/labs/${encodeURIComponent(labId)}/hint`, undefined, signal),

  revealHint: (labId: string) =>
    post<HintResponse>(`/api/course/labs/${encodeURIComponent(labId)}/hint`),

  // ---------------------------------------------------------------- sandbox
  sandboxStatus: (signal?: AbortSignal) =>
    get<SandboxStatus>("/api/sandbox/status", undefined, signal),

  sandboxStart: () => post<{ running: true; restored: boolean }>("/api/sandbox/start"),

  sandboxStop: () => post<{ ok: true }>("/api/sandbox/stop"),

  sandboxBackup: () => post<{ ok: boolean; backupId?: string }>("/api/sandbox/backup"),

  openLab: (labId: string) =>
    post<{ path: string; created: boolean }>(`/api/sandbox/labs/${encodeURIComponent(labId)}/open`),

  resetLab: (labId: string) =>
    post<{ ok: true }>(`/api/sandbox/labs/${encodeURIComponent(labId)}/reset`),

  gradeLab: (labId: string) =>
    post<GradeResult>(`/api/sandbox/labs/${encodeURIComponent(labId)}/grade`),

  submitLab: (labId: string) =>
    post<SubmitResponse>(`/api/sandbox/labs/${encodeURIComponent(labId)}/submit`),

  mySubmissions: (signal?: AbortSignal) =>
    get<{ submissions: CapstoneSubmissionMine[] }>(
      "/api/sandbox/capstone/submissions", undefined, signal,
    ),

  listDir: (path: string, signal?: AbortSignal) =>
    get<{ entries: FsEntry[] }>("/api/sandbox/fs", { path }, signal),

  readFile: (path: string, signal?: AbortSignal) =>
    get<FileContent>("/api/sandbox/fs/file", { path }, signal),

  writeFile: (path: string, content: string) =>
    request<{ ok: true }>("/api/sandbox/fs/file", { method: "PUT", body: { path, content } }),

  mkdir: (path: string) => post<{ ok: true }>("/api/sandbox/fs/mkdir", { path }),

  renamePath: (from: string, to: string) => post<{ ok: true }>("/api/sandbox/fs/rename", { from, to }),

  removePath: (path: string) =>
    request<{ ok: true }>("/api/sandbox/fs", { method: "DELETE", query: { path } }),

  run: (argv: string[], cwd?: string) =>
    post<{ exitCode: number; stdout: string; stderr: string }>("/api/sandbox/run", { argv, cwd }),

  // ---------------------------------------------------------------- certificates
  myCertificate: (courseId: string, signal?: AbortSignal) =>
    get<CertificateResponse>("/api/certificates/mine", { course: courseId }, signal),

  claimCertificate: (courseId: string) =>
    post<{ certificate: { id: string; issuedAt: number; name: string } }>(
      "/api/certificates/claim", { course: courseId },
    ),

  // ---------------------------------------------------------------- admin
  adminOverview: (signal?: AbortSignal) =>
    get<AdminOverview>("/api/admin/overview", undefined, signal),

  adminUsers: (query: { q?: string; page?: number }, signal?: AbortSignal) =>
    get<AdminUsersResponse>("/api/admin/users", query, signal),

  adminUser: (id: string, signal?: AbortSignal) =>
    get<AdminLearnerDetail>(`/api/admin/users/${encodeURIComponent(id)}`, undefined, signal),

  adminUpdateUser: (id: string, body: { disabled?: boolean; role?: Role; displayName?: string }) =>
    request<{ ok: true }>(`/api/admin/users/${encodeURIComponent(id)}`, { method: "PATCH", body }),

  adminResetMfa: (id: string) =>
    post<{ ok: true; message: string }>(`/api/admin/users/${encodeURIComponent(id)}/mfa/reset`),

  adminRevokeSessions: (id: string) =>
    post<{ ok: true }>(`/api/admin/users/${encodeURIComponent(id)}/sessions/revoke`),

  adminPasswordResetLink: (id: string) =>
    post<{ url: string; expiresAt: number }>(
      `/api/admin/users/${encodeURIComponent(id)}/password-reset-link`,
    ),

  adminStopSandbox: (id: string) =>
    post<{ ok: true }>(`/api/admin/users/${encodeURIComponent(id)}/sandbox/stop`),

  adminInvites: (signal?: AbortSignal) =>
    get<{ invites: InviteRow[] }>("/api/admin/invites", undefined, signal),

  adminCreateInvite: (body: { email: string; role: Role }) =>
    post<{ invite: CreatedInvite }>("/api/admin/invites", body),

  adminDeleteInvite: (id: string) =>
    request<{ ok: true }>(`/api/admin/invites/${encodeURIComponent(id)}`, { method: "DELETE" }),

  adminCapstones: (signal?: AbortSignal) =>
    get<{ submissions: CapstoneRow[] }>("/api/admin/capstones", undefined, signal),

  adminCapstone: (id: string, signal?: AbortSignal) =>
    get<CapstoneDetail>(`/api/admin/capstones/${encodeURIComponent(id)}`, undefined, signal),

  adminCapstoneFile: (id: string, path: string, signal?: AbortSignal) =>
    get<{ path: string; content: string }>(
      `/api/admin/capstones/${encodeURIComponent(id)}/file`, { path }, signal,
    ),

  adminReviewCapstone: (
    id: string, body: { reviewScore: number; feedback: string; pass: boolean },
  ) => post<{ ok: true }>(`/api/admin/capstones/${encodeURIComponent(id)}/review`, body),

  adminSettings: (signal?: AbortSignal) =>
    get<Record<string, string>>("/api/admin/settings", undefined, signal),

  adminUpdateSettings: (body: { allowSignup?: boolean; capstoneRequiresReview?: boolean; discordUrl?: string }) =>
    request<Record<string, string>>("/api/admin/settings", { method: "PATCH", body }),

  adminAudit: (page: number, signal?: AbortSignal) =>
    get<{ events: AuditEvent[]; page: number }>("/api/admin/audit", { page }, signal),

  // ---------------------------------------------------------------- feedback
  sendFeedback: (body: { kind: FeedbackKind; message: string; page?: string }) =>
    post<{ ok: true; id: string }>("/api/feedback", body),

  myFeedback: (signal?: AbortSignal) => get<{ feedback: FeedbackItem[] }>("/api/feedback", undefined, signal),

  adminFeedback: (status: FeedbackStatus | "", signal?: AbortSignal) =>
    get<{ feedback: FeedbackAdminItem[]; counts: Partial<Record<FeedbackStatus, number>> }>(
      "/api/admin/feedback", status ? { status } : undefined, signal,
    ),

  adminUpdateFeedback: (id: string, body: { status?: FeedbackStatus; reply?: string }) =>
    request<{ ok: true; emailed: boolean }>(`/api/admin/feedback/${encodeURIComponent(id)}`, { method: "PATCH", body }),

  adminDeleteFeedback: (id: string) =>
    request<{ ok: true }>(`/api/admin/feedback/${encodeURIComponent(id)}`, { method: "DELETE" }),

  // ---------------------------------------------------------------- public site (terraformed.dev)
  adminRequests: (status: RequestStatus | "", signal?: AbortSignal) =>
    get<{ requests: TutorialRequest[]; counts: Partial<Record<RequestStatus, number>> }>(
      "/api/admin/community/requests", status ? { status } : undefined, signal,
    ),

  adminUpdateRequest: (id: string, status: RequestStatus) =>
    request<{ ok: true }>(`/api/admin/community/requests/${encodeURIComponent(id)}`, { method: "PATCH", body: { status } }),

  adminDeleteRequest: (id: string) =>
    request<{ ok: true }>(`/api/admin/community/requests/${encodeURIComponent(id)}`, { method: "DELETE" }),

  adminForumThreads: (filter: "all" | "hidden", signal?: AbortSignal) =>
    get<{ threads: ForumThreadAdmin[] }>("/api/admin/community/forum", filter === "hidden" ? { filter } : undefined, signal),

  adminForumPosts: (threadId: string, signal?: AbortSignal) =>
    get<{ posts: ForumPostAdmin[] }>(`/api/admin/community/forum/threads/${encodeURIComponent(threadId)}`, undefined, signal),

  adminUpdateThread: (id: string, body: { hidden?: boolean; locked?: boolean }) =>
    request<{ ok: true }>(`/api/admin/community/forum/threads/${encodeURIComponent(id)}`, { method: "PATCH", body }),

  adminUpdatePost: (id: string, hidden: boolean) =>
    request<{ ok: true }>(`/api/admin/community/forum/posts/${encodeURIComponent(id)}`, { method: "PATCH", body: { hidden } }),

  adminDeleteThread: (id: string) =>
    request<{ ok: true }>(`/api/admin/community/forum/threads/${encodeURIComponent(id)}`, { method: "DELETE" }),
};

/** Compiled lesson content, cached per module for the life of the page. */
const contentCache = new Map<string, Promise<ModuleContent>>();

export function loadModuleContent(moduleId: string): Promise<ModuleContent> {
  const cached = contentCache.get(moduleId);
  if (cached) return cached;
  const pending = (async () => {
    const res = await fetch(`/content/${encodeURIComponent(moduleId)}.json`, {
      credentials: "same-origin",
      headers: { accept: "application/json" },
    }).catch(() => {
      throw new ApiError(0, "network", "The lesson content could not be downloaded. Check your connection.");
    });
    if (!res.ok) {
      let code = `http_${res.status}`;
      let message = "This module's content could not be loaded.";
      try {
        const body = await res.json() as { error?: string; message?: string };
        if (body.error) code = body.error;
        if (body.message) message = body.message;
      } catch { /* a non-JSON error body is fine; the defaults say enough */ }
      const error = new ApiError(res.status, code, message);
      if (error.isAuthProblem) authProblemHandler?.();
      throw error;
    }
    // A module id that does not exist is served the single-page-app shell by the assets
    // binding, so an unparseable body here means "no such module" rather than a broken server.
    const text = await res.text();
    try {
      return JSON.parse(text) as ModuleContent;
    } catch {
      throw new ApiError(404, "not_found", "There is no module with that id.");
    }
  })();
  contentCache.set(moduleId, pending);
  pending.catch(() => contentCache.delete(moduleId));
  return pending;
}

/** The sandbox terminal WebSocket URL for the current origin. */
export function terminalUrl(params: {
  cols: number;
  rows: number;
  session: string;
  cwd?: string;
}): string {
  const url = new URL("/api/sandbox/terminal", window.location.href);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.searchParams.set("cols", String(params.cols));
  url.searchParams.set("rows", String(params.rows));
  url.searchParams.set("session", params.session);
  if (params.cwd) url.searchParams.set("cwd", params.cwd);
  return url.toString();
}

export const progressCsvUrl = "/api/admin/export/progress.csv";
